import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry, DateFilterModule } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../../../../lib/axios';
import { 
    Box, Typography, CircularProgress, Tooltip, IconButton, Button, 
    Chip, Card, CardContent, Grid, TextField, InputAdornment, 
    Radio, FormControlLabel, FormControl, Paper, Switch, Drawer,
    BottomNavigation, BottomNavigationAction, Stack, Collapse,
    useMediaQuery, useTheme, Badge, Avatar, Divider
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { selectGlobalProperty, setGlobalProperty } from '../../../../redux/propertySlice';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import '../../../../AgGridStyle.css';
import HomeIcon from '@mui/icons-material/Home';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import TodayIcon from '@mui/icons-material/Today';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InventoryIcon from '@mui/icons-material/Inventory';
import EditIcon from '@mui/icons-material/Edit';
import TimerIcon from '@mui/icons-material/Timer';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';

ModuleRegistry.registerModules([AllCommunityModule, DateFilterModule]);

const TiffinReturnTable = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [rowData, setRowData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [timers, setTimers] = useState({});
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    
    const UserId = sessionStorage?.getItem('UserId');
    const gridRef = useRef();
    const navigate = useNavigate();
    const globalProperty = useSelector(selectGlobalProperty);
    const dispatch = useDispatch();
    const [isPropertyReady, setIsPropertyReady] = useState(false);
    
    const [activeRadio, setActiveRadio] = useState('timeRange');
    const [viewByResident, setViewByResident] = useState(false);
    const [timeRangeToday, setTimeRangeToday] = useState(true);
    
    const [isMobileView, setIsMobileView] = useState(false);

    useEffect(() => {
        setIsMobileView(isMobile);
    }, [isMobile]);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobileView(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const initializeProperty = async () => {
            const savedPropertyId = localStorage.getItem('selectedPropertyId');
            const isLocked = localStorage.getItem('propertyLocked') === 'true';
            
            if (savedPropertyId && isLocked && !globalProperty) {
                dispatch(setGlobalProperty(parseInt(savedPropertyId)));
            }
            
            setIsPropertyReady(true);
        };
        
        initializeProperty();
    }, [dispatch, globalProperty]);

    // Timer effect for 10-minute edit window
    useEffect(() => {
        const interval = setInterval(() => {
            const newTimers = {};
            const now = new Date();

            rowData.forEach(order => {
                if (order.Tfn_ReqSt?.toLowerCase() === 'delivered' && order.Dlv_Act_Time) {
                    const deliveryTime = new Date(order.Dlv_Act_Time);
                    const timeDiff = (now - deliveryTime) / 1000 / 60;
                    const remainingTime = Math.max(0, 10 - timeDiff);

                    if (remainingTime > 0) {
                        newTimers[order.Tfn_ReqId] = {
                            remaining: remainingTime,
                            canEdit: true
                        };
                    } else {
                        newTimers[order.Tfn_ReqId] = {
                            remaining: 0,
                            canEdit: false
                        };
                    }
                }
            });

            setTimers(newTimers);
        }, 1000);

        return () => clearInterval(interval);
    }, [rowData]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        return `${hh}:${min}`;
    };

    const formatTimeOnly = (dateTimeString) => {
        if (!dateTimeString) return '';
        const date = new Date(dateTimeString);
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        return `${hh}:${min}`;
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'booked':
                return '#22c55e';
            case 'pending':
                return '#f59e0b';
            case 'cancelled':
                return '#ef4444';
            case 'delivered':
                return '#3b82f6';
            default:
                return '#6b7280';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'booked':
                return <CheckCircleIcon sx={{ fontSize: 14, color: '#22c55e' }} />;
            case 'pending':
                return <ScheduleIcon sx={{ fontSize: 14, color: '#f59e0b' }} />;
            case 'cancelled':
                return <CancelIcon sx={{ fontSize: 14, color: '#ef4444' }} />;
            case 'delivered':
                return <DeliveryDiningIcon sx={{ fontSize: 14, color: '#3b82f6' }} />;
            default:
                return null;
        }
    };

    const TimerDisplay = ({ orderId }) => {
        const timer = timers[orderId];
        
        if (!timer || timer.remaining <= 0) {
            return (
                <Chip
                    size="small"
                    icon={<TimerIcon sx={{ fontSize: 12 }} />}
                    label="Edit Expired"
                    sx={{
                        backgroundColor: '#fee2e2',
                        color: '#dc2626',
                        fontWeight: 600,
                        fontSize: '10px',
                        height: '24px'
                    }}
                />
            );
        }
        
        const minutes = Math.floor(timer.remaining);
        const seconds = Math.floor((timer.remaining % 1) * 60);
        
        return (
            <Chip
                size="small"
                icon={<TimerIcon sx={{ fontSize: 12 }} />}
                label={`${minutes}:${seconds.toString().padStart(2, '0')}`}
                sx={{
                    backgroundColor: '#fef3c7',
                    color: '#d97706',
                    fontWeight: 600,
                    fontSize: '10px',
                    height: '24px',
                    animation: 'pulse 1s infinite',
                    '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.7 },
                        '100%': { opacity: 1 }
                    }
                }}
            />
        );
    };

    const colDefs = useMemo(() => [
        { field: 'RowNum', headerName: 'Sr.No', width: 50, valueGetter: (params) => params.node.rowIndex + 1 },
        { field: 'Tfn_ReqId', headerName: 'Order ID', width: 60, filter: 'agNumberColumnFilter' },
        { field: 'ResidentName', headerName: 'Resident', width: 100 },
         { field: 'InstituteName', headerName: 'Institute', width: 140 },
        { field: 'RoomBedName', headerName: 'Room No', width: 80 },
        { field: 'Req_Date', headerName: 'Order Date', width: 130, valueFormatter: (params) => formatDate(params.value), filter: 'agDateColumnFilter' },
        { field: 'MealTypeName', headerName: 'Meal Type', width: 90, cellRenderer: (params) => (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><FastfoodIcon sx={{ fontSize: 14, color: '#635bff' }} /><span>{params.value || 'N/A'}</span></Box>) },
        { field: 'InstituteName', headerName: 'Institute', width: 120 },
        { field: 'Dlv_Loc', headerName: 'Delivery Location', width: 120, cellRenderer: (params) => (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LocationOnIcon sx={{ fontSize: 14, color: '#f97316' }} /><span>{params.value || 'N/A'}</span></Box>) },
        { field: 'Req_Qty', headerName: 'Qty', width: 50, filter: 'agNumberColumnFilter', cellStyle: { fontWeight: 'bold', textAlign: 'center' } },
        { field: 'RouteNo', headerName: 'Route No', width: 80, filter: 'agNumberColumnFilter', cellStyle: { fontWeight: 'bold', textAlign: 'center' } },
        { field: 'Dlv_Qty', headerName: 'Delivered', width: 70, cellStyle: { fontWeight: 'bold', textAlign: 'center', color: '#22c55e' } },
        { field: 'Rtn_Qty', headerName: 'Returned', width: 70, cellStyle: { fontWeight: 'bold', textAlign: 'center', color: '#f59e0b' } },
        { field: 'Dlv_Time', headerName: 'Schedule Time', width: 90, valueFormatter: (params) => formatTime(params.value) },
        { field: 'Dlv_Act_Time', headerName: 'Actual Delivery', width: 130, valueFormatter: (params) => params.value ? formatDate(params.value) : '-' },
        { field: 'Tfn_ReqSt', headerName: 'Status', width: 110, cellRenderer: (params) => (<Chip size="small" icon={getStatusIcon(params.value)} label={params.value || 'N/A'} sx={{ backgroundColor: `${getStatusColor(params.value)}20`, color: getStatusColor(params.value), fontWeight: 600, height: '26px', '& .MuiChip-icon': { color: 'inherit' } }} />) },
        { field: 'EditWindow', headerName: 'Edit Window', width: 130, cellRenderer: (params) => { if (params.data.Tfn_ReqSt?.toLowerCase() === 'delivered' && params.data.Dlv_Act_Time) return <TimerDisplay orderId={params.data.Tfn_ReqId} />; return <Chip size="small" label="Completed" sx={{ bgcolor: '#e5e7eb', color: '#6b7280', height: '24px' }} />; } },
        { field: 'Actions', headerName: 'Actions', width: 130, cellRenderer: (params) => { const timer = timers[params.data.Tfn_ReqId]; const canEdit = timer?.canEdit && timer?.remaining > 0; if (params.data.Tfn_ReqSt?.toLowerCase() === 'delivered' && canEdit) return (<Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleEditOrder(params.data)} sx={{ borderRadius: '20px', textTransform: 'none', borderColor: '#f59e0b', color: '#f59e0b', fontSize: '11px', py: 0.5 }}>Edit</Button>); else return (<Button size="small" variant="contained" startIcon={<InventoryIcon />} onClick={() => handleReturnOrder(params.data)} sx={{ borderRadius: '20px', textTransform: 'none', bgcolor: '#f59e0b', fontSize: '11px', py: 0.5, '&:hover': { bgcolor: '#d97706' } }}>Return</Button>); } },
        { field: 'Rtn_Remark', headerName: 'Return Remarks', width: 200, tooltipField: 'Rtn_Remark' },
    ], [timers]);

    const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true, floatingFilter: true, width: 100, tooltipValueGetter: (params) => params.value ?? '' }), []);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredData(rowData);
        } else {
            const filtered = rowData.filter(order =>
                order.Tfn_ReqId?.toString().includes(searchTerm) ||
                order.InstituteName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.ResidentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 order.RouteNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.RoomBedName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.Dlv_Loc?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.MealTypeName?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredData(filtered);
        }
    }, [searchTerm, rowData]);

    const fetchReturnOrders = async () => {
        if (!isPropertyReady) return;
        if (!globalProperty) {
            toast.warning('Please select a property first');
            setIsLoading(false);
            return;
        }
        if (!UserId) {
            toast.error('User not found. Please login again.');
            return;
        }

        setIsLoading(true);
        try {
            let flag = "";
            let id = 1;
            
            if (activeRadio === 'viewBy') {
                if (viewByResident) {
                    flag = timeRangeToday ? "ResTodayOrder" : "ResTodayOrder";
                } else {
                    flag = timeRangeToday ? "InsTodayOrder" : "InsTodayOrder";
                }
                id = 1;
            } else {
                if (timeRangeToday) {
                    flag = viewByResident ? "TodayReturn" : "ReturnTodayOrder";
                } else {
                    flag = viewByResident ? "AllReturn" : "ReturnOrder";
                }
                id = 1;
            }
            
            const payload = { Id: id, Flag: flag, PropId: parseInt(globalProperty) };
            const response = await axiosInstance.post('TrnTiffin/RetriveTrnTiffin', payload);

            if (response.data?.Data && response.data.Data.length > 0) {
                setRowData(response.data.Data);
                setFilteredData(response.data.Data);
            } else {
                setRowData([]);
                setFilteredData([]);
                if (response.data?.Data?.length === 0) toast.info(`No return orders found`);
            }
        } catch (error) {
            console.error('Error fetching return orders:', error);
            toast.error(error.response?.data?.Message || 'Error fetching orders');
            setRowData([]);
            setFilteredData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRadioChange = (radioName) => { setActiveRadio(radioName); };
    const handleViewByToggle = (event) => { setViewByResident(event.target.checked); setActiveRadio('viewBy'); };
    const handleTimeRangeToggle = (event) => { setTimeRangeToday(event.target.checked); setActiveRadio('timeRange'); };

    useEffect(() => {
        if (isPropertyReady && globalProperty) fetchReturnOrders();
    }, [activeRadio, viewByResident, timeRangeToday, isPropertyReady, globalProperty]);

    const handleReturnOrder = useCallback((orderData) => {
        navigate('/TiffinReturn', {
            state: { mode: 'return', orderData: orderData }
        });
    }, [navigate]);

    const handleEditOrder = useCallback((orderData) => {
        const timer = timers[orderData.Tfn_ReqId];
        if (timer && timer.canEdit && timer.remaining > 0) {
            navigate('/TiffinDeliveryPerson', {
                state: { mode: 'delivery', orderData: orderData, isEdit: true }
            });
        } else {
            toast.error('Edit window has expired.');
        }
    }, [navigate, timers]);

    const handleOrderClick = useCallback((orderData) => {
        const isDelivered = orderData.Tfn_ReqSt?.toLowerCase() === 'delivered';
        const timer = timers[orderData.Tfn_ReqId];
        const canEdit = isDelivered && timer?.canEdit && timer?.remaining > 0;

        if (isDelivered && canEdit) {
            handleEditOrder(orderData);
        } else {
            handleReturnOrder(orderData);
        }
    }, [handleEditOrder, handleReturnOrder, timers]);

    const handleClearFilters = () => {
        if (gridRef.current?.api) gridRef.current.api.setFilterModel(null);
        setSearchTerm('');
    };

    // Mobile Card Component - Updated Resident & Room Info section
const MobileReturnCard = ({ order }) => {
    const isDelivered = order.Tfn_ReqSt?.toLowerCase() === 'delivered';
    const timer = timers[order.Tfn_ReqId];
    const canEdit = isDelivered && timer?.canEdit && timer?.remaining > 0;
    const missing = (order.Dlv_Qty || 0) - (order.Rtn_Qty || 0);
    
    // Check if ResidentName is empty or just whitespace
    const hasValidResident = order.ResidentName && order.ResidentName.trim() !== '';
    const hasValidRoom = order.RoomBedName && order.RoomBedName.trim() !== '';
    
    return (
        <Card sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
            {/* Status Bar */}
            <Box sx={{ 
                height: 4, 
                background: getStatusColor(order.Tfn_ReqSt),
                opacity: 0.7
            }} />
            
            <CardContent sx={{ p: 1.5 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#635bff20' }}>
                            <ReceiptLongIcon sx={{ fontSize: 16, color: '#635bff' }} />
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#635bff' }}>
                                #{order.Tfn_ReqId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatDate(order.Req_Date)}
                            </Typography>
                        </Box>
                    </Box>
                    <Chip size="small" icon={getStatusIcon(order.Tfn_ReqSt)} label={order.Tfn_ReqSt || 'N/A'} sx={{ backgroundColor: `${getStatusColor(order.Tfn_ReqSt)}20`, color: getStatusColor(order.Tfn_ReqSt), fontWeight: 600, height: 24, fontSize: '11px' }} />
                </Box>

                {/* Resident/Institute & Room Info - Updated */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, p: 1, bgcolor: '#f8f9fc', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {hasValidResident ? (
                            <>
                                <PeopleIcon sx={{ fontSize: 14, color: '#635bff' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>{order.ResidentName}</Typography>
                            </>
                        ) : (
                            <>
                                <BusinessIcon sx={{ fontSize: 14, color: '#635bff' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>{order.InstituteName || 'N/A'}</Typography>
                            </>
                        )}
                    </Box>
                    {hasValidRoom && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon sx={{ fontSize: 14, color: '#f97316' }} />
                            <Typography variant="body2" sx={{ fontSize: '13px' }}>Room: {order.RoomBedName}</Typography>
                        </Box>
                    )}
                </Box>

                {/* Institute Info - Only show if Resident is valid and Institute is different */}
                {hasValidResident && order.InstituteName && (
                    <Box sx={{ mb: 1.5, p: 1, bgcolor: '#f0f0ff', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">Institute</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>{order.InstituteName}</Typography>
                    </Box>
                )}

                {/* Meal & Quantity Info */}
                <Grid container spacing={1} sx={{ mb: 1.5 }}>
                    <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Meal Type</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <FastfoodIcon sx={{ fontSize: 12, color: '#635bff' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '12px' }}>{order.MealTypeName || 'N/A'}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Schedule</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px', mt: 0.5 }}>{formatTimeOnly(order.Dlv_Time)}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Quantity</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#635bff', fontSize: '16px', mt: 0.5 }}>{order.Req_Qty}</Typography>
                    </Grid>
                </Grid>

                {/* Delivery Location */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, p: 1, bgcolor: '#fff7ed', borderRadius: 1 }}>
                    <LocationOnIcon sx={{ fontSize: 14, color: '#f97316' }} />
                    <Typography variant="body2" sx={{ fontSize: '12px', flex: 1 }}>{order.Dlv_Loc}</Typography>
                    <Typography variant="body2" sx={{ fontSize: '13px' }}>Route No: {order.RouteNo || 'N/A'}</Typography>
                </Box>

                {/* Delivery & Return Quantities */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <Box sx={{ flex: 1, p: 1, bgcolor: '#f0fdf4', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600 }}>Delivered</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#22c55e', fontSize: '18px' }}>{order.Dlv_Qty || 0}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, p: 1, bgcolor: '#fef3c7', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 600 }}>Returned</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#d97706', fontSize: '18px' }}>{order.Rtn_Qty || 0}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, p: 1, bgcolor: '#fee2e2', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 600 }}>Missing</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '18px' }}>{missing > 0 ? missing : 0}</Typography>
                    </Box>
                </Box>

                {/* Delivery Info */}
                {order.Dlv_Act_Time && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, p: 1, bgcolor: '#f0fdf4', borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600 }}>Delivered at:</Typography>
                        <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 500 }}>{formatDate(order.Dlv_Act_Time)}</Typography>
                    </Box>
                )}

                {/* Return Remarks */}
                {order.Rtn_Remark && (
                    <Box sx={{ mb: 1.5, p: 1, bgcolor: '#fef3c7', borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 600 }}>Remarks:</Typography>
                        <Typography variant="body2" sx={{ fontSize: '12px' }}>{order.Rtn_Remark}</Typography>
                    </Box>
                )}

                {/* Edit Window & Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    {isDelivered && <TimerDisplay orderId={order.Tfn_ReqId} />}
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        {isDelivered && canEdit ? (
                            <Button size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: 16 }} />} onClick={(e) => { e.stopPropagation(); handleEditOrder(order); }} sx={{ borderRadius: '20px', textTransform: 'none', borderColor: '#f59e0b', color: '#f59e0b', fontSize: '12px', px: 2 }}>Edit</Button>
                        ) : (
                            <Button size="small" variant="contained" startIcon={<InventoryIcon sx={{ fontSize: 16 }} />} onClick={(e) => { e.stopPropagation(); handleReturnOrder(order); }} sx={{ borderRadius: '20px', textTransform: 'none', bgcolor: '#f59e0b', fontSize: '12px', px: 2, '&:hover': { bgcolor: '#d97706' } }}>Return</Button>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

    // Mobile Filter Drawer
    const MobileFilterDrawer = () => (
        <Drawer anchor="bottom" open={mobileFilterOpen} onClose={() => setMobileFilterOpen(false)} PaperProps={{ sx: { borderRadius: '20px 20px 0 0', maxHeight: '70vh' } }}>
            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700}>Filters</Typography>
                    <IconButton onClick={() => setMobileFilterOpen(false)} size="small"><CloseIcon /></IconButton>
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                {/* View By Section */}
                <Paper sx={{ p: 0.5, mb: 2, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Radio checked={activeRadio === 'viewBy'} onChange={() => handleRadioChange('viewBy')} size="small" />
                        <Typography variant="subtitle2" fontWeight={600}>Order By</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ml: 4 }}>
                        <Typography variant="caption" color="text.secondary">Institute</Typography>
                        <Switch checked={viewByResident} onChange={handleViewByToggle} disabled={activeRadio === 'timeRange'} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#635bff' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#635bff' } }} />
                        <Typography variant="caption" color="text.secondary">Resident</Typography>
                    </Box>
                </Paper>
                
                {/* Time Range Section */}
                <Paper sx={{ p: 0.5, mb: 2, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Radio checked={activeRadio === 'timeRange'} onChange={() => handleRadioChange('timeRange')} size="small" />
                        <Typography variant="subtitle2" fontWeight={600}>Time Range</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ml: 4 }}>
                        <Typography variant="caption" color="text.secondary">All Orders</Typography>
                        <Switch checked={timeRangeToday} onChange={handleTimeRangeToggle} disabled={activeRadio === 'viewBy'} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#635bff' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#635bff' } }} />
                        <Typography variant="caption" color="text.secondary">Today's Orders</Typography>
                    </Box>
                </Paper>
                
                <Button fullWidth variant="contained" onClick={() => setMobileFilterOpen(false)} sx={{ bgcolor: '#635bff', borderRadius: 2, mt: 1 }}>Apply Filters</Button>
            </Box>
        </Drawer>
    );

    // Mobile Search Bar
    const MobileSearchBar = () => (
        <Collapse in={mobileSearchOpen}>
            <Box sx={{ px: 2, pb: 2 }}>
                <TextField fullWidth size="small" placeholder="Search by Order ID, Resident, Room, Location..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: '#635bff', fontSize: 18 }} /></InputAdornment>), endAdornment: searchTerm && (<IconButton size="small" onClick={() => setSearchTerm('')}><CloseIcon fontSize="small" /></IconButton>) }} sx={{ bgcolor: 'white', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
        </Collapse>
    );

    // Show loading while property is being initialized
    if (!isPropertyReady) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress sx={{ color: '#635bff' }} />
                <Typography sx={{ ml: 2 }}>Initializing...</Typography>
            </Box>
        );
    }

    // Show warning if no property selected
    if (!globalProperty) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" color="error">No Property Selected</Typography>
                <Typography variant="body1" color="text.secondary">Please select a property to view return orders.</Typography>
                <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ bgcolor: '#635bff' }}>Go to Dashboard</Button>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', minHeight: '90vh', padding: { xs: '8px', sm: '10px' }, backgroundColor: '#f5f7fa', position: 'relative', pb: { xs: 8, sm: 2 } }}>
            <ToastContainer position="bottom-center" style={{ fontSize: '12px' }} />
            
            {/* Desktop Header */}
            {!isMobileView ? (
                <Box sx={{ backgroundColor: "#ffffff", padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)", borderRadius: "12px", margin: "10px auto", maxWidth: "95%", gap: 2 }}>
                    <Breadcrumbs separator="›" sx={{ fontSize: "14px" }}>
                        <Link underline="hover" color="#635bff" sx={{ cursor: "pointer", display: "flex", alignItems: "center" }} onClick={() => navigate('/dashboard')}><HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />Home</Link>
                        <Link underline="hover" color="#635bff" sx={{ cursor: "pointer" }} onClick={() => navigate('/food-transport')}>Food & Transport</Link>
                        <Typography color="text.primary" sx={{ fontWeight: "500" }}>Return Orders</Typography>
                    </Breadcrumbs>
                    
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Paper sx={{ p: 0.5, borderRadius: 2, bgcolor: activeRadio === 'viewBy' ? '#f0f0ff' : '#f8f9fa', border: activeRadio === 'viewBy' ? '2px solid #635bff' : '1px solid #e0e0e0', transition: 'all 0.3s ease', cursor: 'pointer' }} onClick={() => handleRadioChange('viewBy')}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 0, py: 0 }}>
                                <Radio checked={activeRadio === 'viewBy'} size="small" sx={{ p: 0.5, color: '#635bff' }} />
                                <Box>
                                    <Typography variant="caption" fontWeight={600}>Order By</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Typography variant="caption">Institute</Typography><Switch checked={viewByResident} onChange={handleViewByToggle} disabled={activeRadio === 'timeRange'} size="small" /><Typography variant="caption">Resident</Typography></Box>
                                </Box>
                            </Box>
                        </Paper>
                        
                        <Paper sx={{ p: 0.5, borderRadius: 2, bgcolor: activeRadio === 'timeRange' ? '#f0f0ff' : '#f8f9fa', border: activeRadio === 'timeRange' ? '2px solid #635bff' : '1px solid #e0e0e0', transition: 'all 0.3s ease', cursor: 'pointer' }} onClick={() => handleRadioChange('timeRange')}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 0, py: 0 }}>
                                <Radio checked={activeRadio === 'timeRange'} size="small" sx={{ p: 0.5, color: '#635bff' }} />
                                <Box>
                                    <Typography variant="caption" fontWeight={600}>Time Range</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Typography variant="caption">All Orders</Typography><Switch checked={timeRangeToday} onChange={handleTimeRangeToggle} disabled={activeRadio === 'viewBy'} size="small" /><Typography variant="caption">Today's</Typography></Box>
                                </Box>
                            </Box>
                        </Paper>
                        
                        <Tooltip title='Clear filters'><Button variant="outlined" onClick={handleClearFilters} size="small" sx={{ borderRadius: "50px", borderColor: "#635bff", color: "#635bff", px: 2 }}>Clear</Button></Tooltip>
                        <Tooltip title='Back'><IconButton onClick={() => navigate('/food-transport')} sx={{ bgcolor: '#635bff', width: 32, height: 32 }}><ArrowBackIcon sx={{ color: 'white', fontSize: 18 }} /></IconButton></Tooltip>
                    </Box>
                </Box>
            ) : (
                // Mobile Header
                <Box sx={{ backgroundColor: "#ffffff", borderRadius: "12px", margin: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", overflow: 'hidden' }}>
                    {/* Top Bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5 }}>
                        <IconButton onClick={() => navigate('/food-transport')} sx={{ bgcolor: '#635bff', width: 32, height: 32 }}><ArrowBackIcon sx={{ color: 'white', fontSize: 18 }} /></IconButton>
                        <Typography variant="subtitle1" fontWeight={700}>Return Orders</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton size="small" onClick={() => setMobileSearchOpen(!mobileSearchOpen)} sx={{ bgcolor: '#f0f0f0' }}><SearchIcon sx={{ fontSize: 18, color: '#635bff' }} /></IconButton>
                            <IconButton size="small" onClick={() => setMobileFilterOpen(true)} sx={{ bgcolor: '#f0f0f0' }}><FilterListIcon sx={{ fontSize: 18, color: '#635bff' }} /></IconButton>
                        </Box>
                    </Box>
                    
                    {/* Search Bar */}
                    <MobileSearchBar />
                </Box>
            )}
            
            {/* Mobile Filter Drawer */}
            <MobileFilterDrawer />
            
            {/* Table/Grid View */}
            <Box className={!isMobileView ? "table-grid ag-theme-alpine" : ""} sx={!isMobileView ? { height: 'calc(100vh - 180px)', width: '95%', margin: '0 auto', position: 'relative' } : { margin: '8px', position: 'relative' }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}><CircularProgress size="3rem" sx={{ color: '#635bff' }} /><Typography sx={{ ml: 2, color: '#635bff' }}>Loading...</Typography></Box>
                ) : (
                    <>
                        {!isMobileView ? (
                            <AgGridReact ref={gridRef} rowData={filteredData} columnDefs={colDefs} defaultColDef={defaultColDef} pagination={true} paginationPageSize={100} onRowDoubleClicked={(params) => handleOrderClick(params.data)} paginationPageSizeSelector={[50, 100, 200]} onGridReady={(params) => params.api.sizeColumnsToFit()} enableCellTextSelection={true} rowHeight={22} headerHeight={30} overlayNoRowsTemplate={'<span>No return orders found</span>'} getRowStyle={(params) => ({ backgroundColor: params.node.rowIndex % 2 === 0 ? '#f1f5fd' : '#ffffff', cursor: 'pointer' })} />
                        ) : (
                            <Box sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', pb: 0 }}>
                                {filteredData.length > 0 ? filteredData.map((order) => (<MobileReturnCard key={order.Tfn_ReqId} order={order} />)) : (<Box sx={{ textAlign: 'center', py: 5 }}><Typography variant="body1" color="text.secondary">{searchTerm ? 'No matching returns found' : 'No return orders found'}</Typography></Box>)}
                            </Box>
                        )}
                    </>
                )}
            </Box>
            
            {/* Mobile Bottom Navigation */}
            {isMobileView && (
                <BottomNavigation sx={{ position: 'fixed', bottom: 0, left: 30, right: 0, bgcolor: 'white', borderTop: '1px solid #e0e0e0', zIndex: 10 }}>
                    <BottomNavigationAction label="Filters" icon={<FilterListIcon />} onClick={() => setMobileFilterOpen(true)} />
                    <BottomNavigationAction label="Search" icon={<SearchIcon />} onClick={() => setMobileSearchOpen(!mobileSearchOpen)} />
                    <BottomNavigationAction label={`${filteredData.length}`} icon={<Badge badgeContent={filteredData.filter(o => o.Rtn_Qty === 0 || !o.Rtn_Qty).length} color="warning"><InventoryIcon /></Badge>} />
                </BottomNavigation>
            )}
            
            {/* Total Count */}
            <Box sx={{ position: { xs: 'relative', sm: 'absolute' }, bottom: { xs: 10, sm: 16 }, left: { xs: '50%', sm: 70 }, transform: { xs: 'translateX(-50%)', sm: 'none' }, fontSize: { xs: '12px', sm: '14px' }, color: 'black', textAlign: 'center', mt: { xs: 2, sm: 0 } }}>Total Returns: <b>{filteredData.length}</b></Box>
        </Box>
    );
};

export default TiffinReturnTable;